import { Fragment } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useChrome } from "./Chrome";
import { Badge, REASONS } from "./primitives";
import { Chevrons } from "./Layout";

/**
 * The console's working shape: a browsable list that stays put, and a detail pane beside it.
 * Each column scrolls on its own so picking the next row never loses your place in the list.
 */
export function MasterDetail({
  title,
  count,
  filters,
  footer,
  children,
  detail,
}: {
  title: string;
  count?: string;
  filters?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  detail: React.ReactNode;
}) {
  const { listHidden, setListHidden } = useChrome();

  // Folded away, the list leaves a rail to bring it back — the detail keeps the whole width.
  if (listHidden) {
    return (
      <div className="flex h-full">
        <button
          type="button"
          className="flex w-8 shrink-0 flex-col items-center gap-3 border-r border-line bg-surface py-3 text-muted transition-colors hover:bg-raised hover:text-strong"
          onClick={() => setListHidden(false)}
          title={`Show the ${title} list  ( ] )`}
        >
          <Chevrons pointing="right" />
          <span className="label [writing-mode:vertical-rl]">{title}</span>
        </button>
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">{detail}</div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col lg:flex-row">
      <div className="flex max-h-72 min-h-0 shrink-0 flex-col border-b border-line lg:h-full lg:max-h-none lg:w-[480px] lg:border-b-0 lg:border-r">
        <div className="shrink-0 border-b border-line bg-surface px-4 py-2.5">
          <div className="flex items-baseline justify-between gap-2">
            <h1 className="panel-title">{title}</h1>
            <span className="flex items-baseline gap-3">
              {count && <span className="mono text-xs text-faint">{count}</span>}
              <button
                type="button"
                className="text-muted transition-colors hover:text-strong"
                onClick={() => setListHidden(true)}
                title="Hide this list  ( ] )"
              >
                <Chevrons pointing="left" />
              </button>
            </span>
          </div>
          {filters && <div className="mt-2 flex flex-wrap items-center gap-2">{filters}</div>}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        {footer && (
          <div className="shrink-0 border-t border-line px-4 py-2 text-[11px] text-faint">
            {footer}
          </div>
        )}
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">{detail}</div>
    </div>
  );
}

/**
 * A list row that reads as selected when its own detail is open.
 *
 * Deliberately a div rather than a link: the row is dense with other identifiers, and each of
 * those has its own destination. Anchors cannot nest, so the row navigates on click and the
 * guids inside it stay real links.
 */
export function ListRow({ to, children }: { to: string; children: React.ReactNode }) {
  const navigate = useNavigate();
  const active = useLocation().pathname === to;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate(to)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          navigate(to);
        }
      }}
      className={`cursor-pointer border-b border-line/70 px-4 py-2.5 transition-colors ${
        active ? "bg-elevated" : "hover:bg-raised"
      }`}
    >
      {children}
    </div>
  );
}

/**
 * The label/value lines under a list row's title. A fixed label column keeps every value on
 * the same left edge — inline pairs wrapped into a ragged staircase.
 */
export function RowFields({ children }: { children: React.ReactNode }) {
  return (
    <dl className="mt-1 grid grid-cols-[62px_1fr] items-baseline gap-x-2 gap-y-0.5 text-[11px]">
      {children}
    </dl>
  );
}

export function RowField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="label text-right">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  );
}

/** The right pane before anything is picked — says what the list answers. */
export function DetailPlaceholder({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-8 p-8">
      <p className="max-w-sm text-center text-sm text-faint">{children}</p>
      <Legend />
    </div>
  );
}

/**
 * The outcome and store legend from ADR-I1 §4.3, parked in the empty detail pane: visible to
 * someone learning the model, gone the moment they pick a row.
 */
function Legend() {
  return (
    <div className="w-full max-w-2xl space-y-4">
      <div>
        <div className="label mb-2">what the apply decided</div>
        <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-xs text-muted">
          <dt>
            <Badge tone="good">applied</Badge>
          </dt>
          <dd>Written to the load and given a cursor, so the device will pull it.</dd>
          <dt>
            <Badge tone="bad">rejected</Badge>
          </dt>
          <dd>
            A driver event not written, and it consumed no cursor — the device never learns about
            it. Also a CTMS create for an entity already stored. The reason code says why.
          </dd>
          <dt>
            <Badge tone="muted">noop</Badge>
          </dt>
          <dd>
            A CTMS event the engine acked without writing — every CTMS refusal except{" "}
            <span className="mono">already_exists</span>, including crashes and envelopes it could
            not parse (those reason codes stay red). The reason code says which.
          </dd>
          <dt>
            <Badge tone="warn">overridden</Badge>
          </dt>
          <dd>Reserved for conflict management, which the engine does not have yet.</dd>
        </dl>
        <p className="mt-2 text-xs text-faint">
          A duplicate event writes no row at all: the event-guid dedupe returns before the journal.
        </p>
      </div>

      <div>
        <div className="label mb-2">reason codes</div>
        <dl className="grid grid-cols-[150px_1fr] gap-x-3 gap-y-1.5 text-xs text-muted">
          {Object.entries(REASONS).map(([code, reason]) => (
            <Fragment key={code}>
              <dt className={`mono ${reason.failure ? "text-bad" : "text-faint"}`}>{code}</dt>
              <dd>
                <span className="text-faint">{reason.path} · </span>
                {reason.meaning}
              </dd>
            </Fragment>
          ))}
        </dl>
      </div>

      <div>
        <div className="label mb-2">which table is behind each screen</div>
        <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-xs text-muted">
          <dt className="mono text-faint">inbox_batches</dt>
          <dd>What a device sent. Batches.</dd>
          <dt className="mono text-faint">change_log</dt>
          <dd>What the apply decided, per event. A load's Timeline.</dd>
          <dt className="mono text-faint">feed_index</dt>
          <dd>What a device can still pull, one row per load. A driver's feed.</dd>
          <dt className="mono text-faint">connection_cursors</dt>
          <dd>The newest cursor per driver and carrier. A driver's connections.</dd>
          <dt className="mono text-faint">applied_batches</dt>
          <dd>How far a device got. A device's acks.</dd>
          <dt className="mono text-faint">outbound_messages</dt>
          <dd>What is queued for CTMS. Outbound.</dd>
        </dl>
      </div>
    </div>
  );
}

export function LoadMore({
  hasNext,
  loading,
  onClick,
  label = "Load more",
}: {
  hasNext: boolean;
  loading: boolean;
  onClick: () => void;
  label?: string;
}) {
  if (!hasNext) return null;
  return (
    <div className="p-3">
      <button type="button" className="btn w-full" onClick={onClick} disabled={loading}>
        {loading ? "loading…" : label}
      </button>
    </div>
  );
}
